import { createSelector } from "@reduxjs/toolkit"
import { get, memoize } from "nodash"
import { selectPanoramaFlightById, selectPanoramaFlights } from "../../flights/selectors"
import { PanoramaImage } from "@nova/protobuf"
import {
    ImageProcessingState,
    ImageWarnings,
    isUsable,
    PanoramaImageWithContext,
    UploadStateSummary,
} from "@nova/constants"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    selectUploadsForSet as selectPanoramaUploadsForSet,
    selectAllUploads as selectAllPanoramaUploads,
} from "plugins/Panorama/PanoramaSlice"
import { panoramaImageS3Key } from "plugins/Panorama/module"

const selectPanoramaImagesMap = createSelector(
    [selectPanoramaFlights, selectActiveWorkspaceId(), selectActivatedFireId],
    (flights, orgId, projectId) => {
        if (!orgId || !projectId) {
            return {}
        }

        return flights.reduce(
            (acc, flight) => {
                if (!orgId || !projectId || !flight || !flight.images?.length) return acc

                const image = flight.images[0]

                if (image) {
                    acc[flight.id] = {
                        ...image,
                        s3Key: panoramaImageS3Key(orgId, projectId, flight.id, image.id),
                        state: ImageProcessingState.IngestDone,
                    } as PanoramaImage
                }

                return acc
            },
            {} as Record<string, PanoramaImage>
        )
    }
)

export const selectPanoramaImageByFlightId = memoize((id: string) =>
    createSelector(
        [selectPanoramaFlightById(id), selectActiveWorkspaceId(), selectActivatedFireId],
        (flight, orgId, projectId) => {
            if (!orgId || !projectId || !flight || !flight.images?.length) {
                return undefined
            }
            const image = flight.images[0]

            return image
                ? ({
                      ...image,
                      s3Key: panoramaImageS3Key(orgId, projectId, flight.id, image.id),
                      state: ImageProcessingState.IngestDone,
                  } as PanoramaImageWithContext)
                : undefined
        }
    )
)

const selectPanoramaImageAndUploadMap = createSelector(
    [selectPanoramaImagesMap, selectAllPanoramaUploads()],
    (imageById, uploadsById) => {
        return { ...uploadsById, ...imageById }
    }
)

export const selectAllPanoramaImagesAndUploads = createSelector(
    [selectPanoramaImageAndUploadMap],
    (imageById) => Object.values(imageById)
)

export const selectPanoramaImageAndUploadByFlightId = memoize((id: string) =>
    createSelector(
        [selectPanoramaImageByFlightId(id), selectPanoramaUploadsForSet(id)],
        (image, uploads) => {
            if (!image) return uploads ?? []
            const activeUpload = uploads?.find((u) => u.id === image.id)
            if (activeUpload) {
                const error = get(activeUpload, "error")
                return [
                    {
                        ...image,
                        state: get(activeUpload, "state"),
                        ...(error !== undefined ? { error: error } : {}),
                    } as unknown as PanoramaImageWithContext,
                ]
            }
            return [image]
        }
    )
)

export const selectPanoramaUploadSize = memoize((id: string) =>
    createSelector([selectPanoramaImageAndUploadByFlightId(id)], (uploads) => {
        let totalSize = 0
        uploads?.forEach((upload) => {
            const size = get(upload, "size", 0)
            totalSize += size
        })
        return totalSize
    })
)

export const selectPanoramaFinishedUploadSize = memoize((id: string) =>
    createSelector([selectPanoramaImageAndUploadByFlightId(id)], (uploads) => {
        let totalSize = 0
        uploads?.forEach((upload) => {
            const state = get(upload, "state")
            const size = get(upload, "size", 0)
            if (state !== ImageProcessingState.IngestDone) return
            totalSize += size
        })
        return totalSize
    })
)

export const selectSummaryFromPanoramaUpload = memoize((id: string) =>
    createSelector([selectPanoramaImageAndUploadByFlightId(id)], (uploads) => {
        const summary: UploadStateSummary = {
            stateCount: {} as Record<ImageProcessingState, number>,
            uploadCount: uploads?.length || 0,
            errorCount: 0,
            warnings: {} as ImageWarnings,
        }
        uploads?.forEach((upload) => {
            const usable = isUsable(upload)
            if (!usable) return summary.errorCount++
            summary.stateCount[usable.state] = ++summary.stateCount[usable.state] || 1
        })
        return summary
    })
)

export const selectIsPanoramaFlightUploading = memoize((id: string) =>
    createSelector([selectSummaryFromPanoramaUpload(id)], (summary) => {
        return (
            !!summary.stateCount?.[ImageProcessingState.PendingUpload] ||
            !!summary.stateCount?.[ImageProcessingState.Uploading]
        )
    })
)
